import React, { useState, useContext, useEffect } from "react";
import styles from "./V2Pricing.module.css";
import SearchIcon from "@mui/icons-material/Search";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { globalContext } from "../../Provider/AppProvider";

interface Ticket {
  id: string;
  marketplaces: string[];
  event: string;
  eventDate: string;
  sectionRow: string;
  currentPrice: number;
  clientName: string;
  autoPrice?: number;
  status: "Self Pricing" | "Concierge";
}

const mockTickets: Ticket[] = [
  {
    id: "1",
    marketplaces: ["BM", "FB", "TM"],
    event: "Taylor Swift Eras Tour",
    eventDate: "02/10/2026",
    sectionRow: "Sec 120, Row 15",
    currentPrice: 250,
    clientName: "John Mitchell",
    autoPrice: 280,
    status: "Self Pricing",
  },
  {
    id: "2",
    marketplaces: ["BM", "FB", "TM"],
    event: "Florida Panthers vs. Chicago Blackhawks",
    eventDate: "04/28/2026",
    sectionRow: "Sec 120, Row 8",
    currentPrice: 150,
    clientName: "Christopher Brown",
    autoPrice: 175,
    status: "Self Pricing",
  },
  {
    id: "3",
    marketplaces: ["BM", "FB", "TM"],
    event: "Post Malone World Tour",
    eventDate: "04/21/2026",
    sectionRow: "Sec 300, Row 18",
    currentPrice: 300,
    clientName: "Sarah Thompson",
    status: "Concierge",
  },
  {
    id: "4",
    marketplaces: ["BM", "FB", "TM"],
    event: "Boston Celtics vs. Miami Heat",
    eventDate: "03/30/2026",
    sectionRow: "Sec 10, Row 11",
    currentPrice: 200,
    clientName: "John Mitchell",
    autoPrice: 220,
    status: "Self Pricing",
  },
  {
    id: "5",
    marketplaces: ["BM", "FB", "TM"],
    event: "Taylor Swift Eras Tour",
    eventDate: "02/16/2026",
    sectionRow: "Sec ORCH, Row 13",
    currentPrice: 500,
    clientName: "Amy Davis",
    autoPrice: 550,
    status: "Self Pricing",
  },
  {
    id: "6",
    marketplaces: ["BM", "FB", "TM"],
    event: "Tampa Bay Lightning vs. Toronto Maple Leafs",
    eventDate: "04/10/2026",
    sectionRow: "Sec ORCH, Row 2",
    currentPrice: 95,
    clientName: "Sarah Thompson",
    status: "Concierge",
  },
];

const V2Pricing: React.FC = () => {
  const { globalState } = useContext<any>(globalContext);
  const [tickets] = useState<Ticket[]>(mockTickets);
  const [search, setSearch] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Filter tickets based on search
  const filteredTickets = tickets.filter((ticket) =>
    ticket.event.toLowerCase().includes(search.toLowerCase()) ||
    ticket.clientName.toLowerCase().includes(search.toLowerCase()) ||
    ticket.sectionRow.toLowerCase().includes(search.toLowerCase())
  );

  // Calculate stats
  const listedTickets = tickets.length;
  const soldTickets = tickets.filter((t) => t.autoPrice).length;
  const pendingVerification = tickets.filter((t) => t.status === "Concierge").length;
  const autoPriced = tickets.filter((t) => t.autoPrice && t.status === "Self Pricing").length;

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className={styles.container}>
      {/* HEADER */}
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>AI-Powered Price Suggestions for Concierge Tickets</p>
          <h1 className={styles.title}>V2: Automated Pricing</h1>
        </div>
        <div className={styles.searchSection}>
          <input
            type="text"
            placeholder="Search user by name/email/phone..."
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className={styles.iconBtn}
            onClick={handleRefresh}
            title="Refresh"
          >
            <RefreshRoundedIcon />
          </button>
          <button
            className={styles.iconBtn}
            onClick={() => setIsDarkMode(!isDarkMode)}
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </button>
        </div>
      </div>

      {/* AUTOMATED PRICING ENGINE SECTION */}
      <div className={styles.engineSection}>
        <div className={styles.engineIcon}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-sparkles w-7 h-7 text-white" aria-hidden="true" x-file-name="App" x-line-number="8192" x-component="Sparkles" x-id="App_8192" x-dynamic="false"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path><path d="M4 17v2"></path><path d="M5 18H3"></path></svg></div>
        <div className={styles.engineContent}>
          <h2 className={styles.engineTitle}>Automated Pricing Engine</h2>
          <p className={styles.engineDescription}>
            Click on any ticket to get AI-powered pricing suggestions based on marketplace data and historical sales
          </p>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{listedTickets}</div>
          <div className={styles.statLabel}>Listed Tickets</div>
        </div>
        <div className={`${styles.statCard} ${styles.soldCard}`}>
          <div className={styles.statNumber}>{soldTickets}</div>
          <div className={styles.statLabel}>Sold Tickets</div>
        </div>
        <div className={`${styles.statCard} ${styles.pendingCard}`}>
          <div className={styles.statNumber}>{pendingVerification}</div>
          <div className={styles.statLabel}>Pending Verification</div>
        </div>
        <div className={`${styles.statCard} ${styles.autoPricedCard}`}>
          <div className={styles.statNumber}>{autoPriced}</div>
          <div className={styles.statLabel}>Auto-Priced</div>
        </div>
      </div>

      {/* TABLE */}
      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{color:"#4f46e5"}}>MARKETPLACES</th>
              <th>EVENT</th>
              <th style={{color:"#4f46e5"}}>SECTION/ROW</th>
              <th style={{color:"orange"}}>CURRENT PRICE</th>
              <th style={{color:"orange"}}>CLIENT</th>
              <th>AUTO-PRICE</th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.map((ticket) => (
              <tr key={ticket.id}>
                <td className={styles.marketplaces}>
                  <div className={styles.marketplaceLogos}>
                    {ticket.marketplaces.map((mp, idx) => (
                      <span key={idx} className={styles.marketplaceBadge}>
                        {mp}
                      </span>
                    ))}
                  </div>
                </td>
                <td className={styles.event}>
                  <div className={styles.eventInfo}>
                    <p className={styles.eventName}>{ticket.event}</p>
                    <p className={styles.eventDate}>{ticket.eventDate}</p>
                  </div>
                </td>
                <td className={styles.sectionRow}>{ticket.sectionRow}</td>
                <td className={styles.price}>${ticket.currentPrice}</td>
                <td className={styles.client}>
                  <div className={styles.clientInfo}>
                    <span className={styles.statusBadge} data-status={ticket.status}>
                      {ticket.status}
                    </span>
                    <p className={styles.clientName}>{ticket.clientName}</p>
                  </div>
                </td>
                <td className={styles.autoPrice}>
                  {ticket.autoPrice ? (
                    <button className={styles.getPriceBtn}>Get Price</button>
                  ) : (
                    <span className={styles.noPriceText}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.tableFooter}>
          Showing {filteredTickets.length} of {listedTickets} tickets
        </div>
      </div>
    </div>
  );
};

export default V2Pricing;
